import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../parts/SvgIcon";
import { formatDateTime, formatTime, initials } from "../door-access-utils";
import LinkButton from "./LinkButton";
import { ResultPill } from "./LogsTable";

const Row = ({ label, support = false, children }) => (
  <div className="door-kv" data-support={support || undefined}>
    <dt>{label}</dt>
    <dd>{children || "–"}</dd>
  </div>
);

/** "EDT" when the zone has a letter abbreviation, else "UTC-4" / "UTC+5:30". */
const zoneLabel = (at) => {
  const abbreviation = at.format("z");
  if (!/^[+-]/.test(abbreviation)) return abbreviation;
  const minutes = Math.abs(at.utcOffset());
  if (!minutes) return "UTC";
  const sign = at.utcOffset() < 0 ? "-" : "+";
  const rest = minutes % 60;
  const suffix = rest ? `:${String(rest).padStart(2, "0")}` : "";
  return `UTC${sign}${Math.floor(minutes / 60)}${suffix}`;
};

const SUB_KEYS = {
  member: "doorAccess.drawer.subMember",
  staff: "doorAccess.drawer.subStaff",
  unmatched: "doorAccess.drawer.subUnmatched",
};

/**
 * Right-side details of one event. The row already carries most of it; the
 * detail request adds why it counts (or not) as a visit and the FaciliKey ids
 * support asks for.
 */
const EventDrawer = ({ api, event, timeZone, onClose }) => {
  const { t } = useTranslation("coaching-portal");
  const [detail, setDetail] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!event) return undefined;
    let cancelled = false;
    setDetail(null);
    setFailed(false);
    api(`door-logs/${event.id}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        setDetail(data?.event || null);
        setFailed(!data?.event);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [api, event]);

  useEffect(() => {
    if (!event) return undefined;
    const onKey = (key) => key.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [event, onClose]);

  if (!event) return null;
  const name = event.personName || event.member?.name || "";
  const counted = detail ? detail.countedAsVisit : event.countedAsVisit;

  let supportBody;
  if (detail) {
    supportBody = (
      <dl className="door-kvs">
        <Row support label={t("doorAccess.drawer.eventId")}>
          {detail.support.facilikeyEventId}
        </Row>
        <Row support label={t("doorAccess.drawer.userId")}>
          {detail.support.userExternalId}
        </Row>
        <Row support label={t("doorAccess.drawer.doorId")}>
          {detail.support.facilikeyDoorId}
        </Row>
        <Row support label={t("doorAccess.drawer.received")}>
          {formatDateTime(detail.support.receivedAt, timeZone)}
        </Row>
      </dl>
    );
  } else if (failed) {
    supportBody = (
      <p className="door-muted">{t("doorAccess.drawer.loadError")}</p>
    );
  } else {
    supportBody = (
      <div className="door-sheet__skeleton" aria-hidden="true">
        <span className="door-skeleton" />
        <span className="door-skeleton" />
        <span className="door-skeleton" />
        <span className="door-skeleton" />
      </div>
    );
  }

  const unmatched = event.personType === "unmatched";
  const zone = zoneLabel(moment.tz(event.occurredAt, timeZone));

  return (
    <>
      <div className="door-scrim" onClick={onClose} aria-hidden="true" />
      <aside //NOSONAR
        className="door-sheet"
        role="dialog"
        aria-label={t("doorAccess.drawer.title")}
      >
        <header className="door-sheet__head">
          <span
            className={`door-avatar door-avatar--xl${unmatched ? " door-avatar--ghost" : ""}`}
            data-staff={event.personType === "staff" || undefined}
          >
            {unmatched ? (
              <SvgIcon
                src="/img/new-icons/vuesax-linear-user.svg"
                useCurrentColor
              />
            ) : (
              initials(name)
            )}
          </span>
          <div className="door-sheet__who">
            <h2>{name}</h2>
            <p>{t(SUB_KEYS[event.personType] || SUB_KEYS.member)}</p>
          </div>
          <button
            type="button"
            className="door-icon-button"
            aria-label={t("doorAccess.drawer.close")}
            onClick={onClose}
          >
            <img src="/img/new-icons/close-modal.svg" alt="" />
          </button>
        </header>

        <div className="door-sheet__body">
          <div className="door-sheet__pill">
            <ResultPill event={event} t={t} />
          </div>

          <dl className="door-kvs">
            <Row label={t("doorAccess.drawer.date")}>
              {formatDateTime(event.occurredAt, timeZone)}
            </Row>
            <Row label={t("doorAccess.drawer.time")}>
              {formatTime(event.occurredAt, timeZone)}{" "}
              <span className="door-muted">{zone}</span>
            </Row>
            <Row label={t("doorAccess.drawer.door")}>{event.door?.name}</Row>
            <Row label={t("doorAccess.drawer.location")}>
              {event.door?.locationName}
            </Row>
            {event.result !== "opened" && (
              <Row label={t("doorAccess.drawer.reason")}>{event.reason}</Row>
            )}
            <Row label={t("doorAccess.drawer.class")}>
              {event.class
                ? `${formatTime(event.class.startsAt, timeZone)} ${event.class.name || ""}`
                : null}
            </Row>
            <Row label={t("doorAccess.drawer.counted")}>
              <b>
                {counted
                  ? t("doorAccess.drawer.yes")
                  : t("doorAccess.drawer.no")}
              </b>
              {detail && (
                <p className="door-muted">
                  {t(`doorAccess.drawer.visitReason.${detail.visitReason}`)}
                </p>
              )}
            </Row>
          </dl>

          <section className="door-sheet__support">
            <h3>{t("doorAccess.drawer.support")}</h3>
            {supportBody}
          </section>
        </div>

        <footer className="door-sheet__foot">
          {event.member ? (
            <LinkButton
              type="secondary"
              href={`/athlete-portal/profile/?athlete=${event.member.id}&section=dashboard`}
            >
              <ProfileLabel t={t} />
            </LinkButton>
          ) : (
            <button type="button" data-button-type="secondary" disabled>
              <ProfileLabel t={t} />
            </button>
          )}
        </footer>
      </aside>
    </>
  );
};

const ProfileLabel = ({ t }) => (
  <>
    <SvgIcon
      src="/img/new-icons/vuesax-linear-user.svg"
      useCurrentColor
      classNames="w-4 h-4 mr-2 align-middle"
    />
    {t("doorAccess.drawer.viewProfile")}
  </>
);

export default EventDrawer;
